import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { Shield, Edit2, Loader2 } from "lucide-react";

const FEATURES = [
  'recipes', 'ingredients', 'materials', 'equipment',
  'inventory', 'production', 'suppliers', 'planner',
  'orders', 'customers', 'menus', 'reports',
  'procurement', 'officeSupplies', 'settings', 'team'
];
const LEVELS = ['none', 'read', 'write', 'approve'];
const ROLES = ['leader', 'manager', 'member', 'viewer'];

const ROLE_COLORS: Record<string, string> = {
  owner: "bg-purple-100 text-purple-800",
  leader: "bg-blue-100 text-blue-800",
  manager: "bg-green-100 text-green-800",
  member: "bg-gray-100 text-gray-800",
  viewer: "bg-yellow-100 text-yellow-800",
};

export function MemberPermissionsEditor() {
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<any>(null);
  const [perms, setPerms] = useState<Record<string, string>>({});
  const [role, setRole] = useState("");
  const [saving, setSaving] = useState(false);

  const wsId = currentWorkspace?._id;

  const { data: members = [], isLoading } = useQuery({
    queryKey: [`/api/workspaces/${wsId}/members`],
    queryFn: () => apiRequest("GET", `/api/workspaces/${wsId}/members`),
    enabled: !!wsId,
  });

  const openEditor = (member: any) => {
    setEditing(member);
    setRole(member.role || "member");
    setPerms(member.permissions || {});
  };

  const save = async () => {
    if (!editing || !wsId) return;
    setSaving(true);
    try {
      await apiRequest("PATCH", `/api/workspaces/${wsId}/members/${editing._id}/permissions`, {
        role, permissions: perms
      });
      toast({ title: "Permissions updated" });
      queryClient.invalidateQueries({ queryKey: [`/api/workspaces/${wsId}/members`] });
      setEditing(null);
    } catch (e: any) {
      toast({ title: e.message || "Failed to update", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) return <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold">Team Members & Permissions</h3>
        <Badge variant="outline">{(members as any[]).length} member(s)</Badge>
      </div>

      {(members as any[]).map((m: any) => (
        <Card key={m._id}>
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <span className="font-medium">{m.email || m.name || "Member"}</span>
                <Badge className={ROLE_COLORS[m.role] || ROLE_COLORS.member}>{m.role}</Badge>
              </div>
              {m.departmentName && <span className="text-xs text-muted-foreground">{m.departmentName}</span>}
            </div>
            {m.role !== "owner" && (
              <Button size="sm" variant="outline" onClick={() => openEditor(m)}>
                <Edit2 className="h-3 w-3 mr-1" /> Permissions
              </Button>
            )}
          </CardContent>
        </Card>
      ))}

      {/* Permission Editor Dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5" /> Edit Permissions — {editing?.email}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLES.map(r => <SelectItem key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="border rounded-lg divide-y">
              <div className="grid grid-cols-2 gap-2 p-2 bg-muted text-xs font-medium">
                <span>Feature</span><span>Access Level</span>
              </div>
              {FEATURES.map(f => (
                <div key={f} className="grid grid-cols-2 gap-2 p-2 items-center">
                  <span className="text-sm capitalize">{f.replace(/([A-Z])/g, ' $1')}</span>
                  <Select value={perms[f] || 'none'} onValueChange={v => setPerms(p => ({ ...p, [f]: v }))}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
